import { defineRecipe } from '@plurall/elo-v3';

// Shared recipe primitives for activity cards (ThemeCard, LearningPathCard, etc.).
// Extracted from ThemeCard — ThemeCard will be migrated to compose from these in a follow-up.

export const activityCardRecipe = defineRecipe({
  className: 'activity-card',
  base: {
    position: 'relative',
    display: 'grid',
    gap: 'gap.none gap.xsmall',
    gridTemplateRows: 'auto auto 1fr auto',
    gridTemplateColumns: '1fr auto',
    blockSize: '100%',
    isolation: 'isolate',
    cursor: 'default',
    bgColor: 'background.primary',
    borderRadius: 'border.radius.xlarge',
    _before: {
      content: "''",
      position: 'absolute',
      inset: 0,
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      background: 'border.primary',
      borderRadius: 'inherit',
      padding: 'border.width.small',
    },
  },
  variants: {
    interactive: {
      true: {
        cursor: 'pointer',
      },
    },
    type: {
      default: {},
      highlight: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr auto auto',
        flex: '0 0 auto',
        inlineSize: '248px',
        blockSize: '312px',
      },
    },
    spacing: {
      default: {},
      compact: {
        paddingBlockEnd: 'padding.small',
      },
    },
    layout: {
      grid: {
        paddingInline: 'padding.xsmall',
        paddingBlockStart: 'padding.xsmall',
        paddingBlockEnd: 'padding.medium',
      },
      list: {
        '--thumbnail-width': '88px',
        gridTemplateColumns: 'var(--thumbnail-width) 1fr auto',
        gridTemplateRows: 'auto 1fr auto',
        padding: 'padding.2xsmall',
        md: {
          '--thumbnail-width': '112px',
          padding: 'padding.small',
        },
      },
    },
  },
  compoundVariants: [
    {
      type: 'highlight',
      layout: 'grid',
      css: { padding: 'padding.none' },
    },
  ],
  defaultVariants: {
    type: 'default',
    spacing: 'default',
    layout: 'grid',
  },
});

export const activityCardHeroRecipe = defineRecipe({
  className: 'activity-card__hero',
  base: {
    gridColumn: '1 / -1',
    gridRow: '1 / span 2',
    position: 'relative',
    padding: 'padding.xsmall',
    borderRadius: 'border.radius.xsmall',
    backgroundColor: 'component.overlay.dimmed.dark',
    overflow: 'clip',
    isolation: 'isolate',
  },
  variants: {
    layout: {
      grid: {
        aspectRatio: '29/15',
        marginBlockEnd: 'padding.small',
      },
      list: {
        gridColumn: '1',
        gridRow: '1 / span 4',
      },
    },
    type: {
      default: {},
      highlight: {
        gridColumn: '1 / -1',
        gridRow: '1 / -1',
        zIndex: '0',
        _after: {
          content: "''",
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to bottom, oklch(from {colors.text.always.dark} l c h / 0%), oklch(from {colors.text.always.dark} l c h / 96%))',
          borderRadius: 'inherit',
          pointerEvents: 'none',
          zIndex: '1',
        },
      },
    },
    spacing: {
      default: {},
      compact: {
        marginBlockEnd: 'padding.xsmall',
      },
    },
  },
  compoundVariants: [
    {
      type: 'highlight',
      layout: 'grid',
      css: {
        aspectRatio: 'auto',
        marginBlockEnd: 'padding.none',
      },
    },
  ],
  defaultVariants: {
    type: 'default',
    layout: 'grid',
    spacing: 'default',
  },
});

export const activityCardImageRecipe = defineRecipe({
  className: 'activity-card__image',
  base: {
    position: 'absolute',
    mixBlendMode: 'multiply',
    inset: '0',
    blockSize: '100%',
    inlineSize: '100%',
    objectFit: 'cover',
  },
});

// Bottom-left overlay slot — sits in grid row 2, overlapping the hero via row-spanning.
// Used for category/type badges or any bottom-left hero overlay content.
export const activityCardBottomLeftRecipe = defineRecipe({
  className: 'activity-card__bottom-left',
  base: {
    gridColumn: '1',
    gridRow: '2',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'gap.2xsmall',
    marginInline: 'padding.xsmall',
    marginBlockStart: 'padding.xsmall',
    marginBlockEnd: 'padding.small',
    paddingBlockEnd: 'padding.xsmall',
    zIndex: '1',
  },
  variants: {
    layout: {
      grid: {},
      list: {
        gridColumn: '2',
        gridRow: '1',
        marginInlineStart: 'padding.medium',
        marginInlineEnd: 'padding.3xsmall',
        marginBlockEnd: 'padding.3xsmall',
        paddingBlockEnd: 'padding.none',
        md: {
          marginInline: 'padding.large',
          marginBlockEnd: 'padding.xsmall',
        },
      },
    },
    type: {
      default: {},
      highlight: {
        gridColumn: '1',
        gridRow: '1',
        zIndex: '1',
        flexDirection: 'column',
        justifyContent: 'space-between',
        justifySelf: 'flex-start',
        paddingBlockStart: 'padding.large',
        paddingInline: 'padding.large',
        paddingBlockEnd: 'padding.none',
        marginInlineStart: 'padding.none',
        marginInlineEnd: 'padding.none',
        marginBlockStart: 'padding.none',
        marginBlockEnd: 'padding.xsmall',
      },
    },
    spacing: {
      default: {},
      compact: {
        marginBlockEnd: 'padding.xsmall',
      },
    },
  },
  defaultVariants: {
    layout: 'grid',
    type: 'default',
    spacing: 'default',
  },
});

// Top-right overlay slot — spans grid column 2 across rows 1–2, above the hero.
// Used for action controls (ThemeCard) or stage progress tags (LearningPathCard).
export const activityCardTopRightRecipe = defineRecipe({
  className: 'activity-card__top-right',
  base: {
    position: 'relative',
    gridColumn: '2',
    gridRow: '1 / 3',
    margin: 'padding.xsmall',
    zIndex: 1,
  },
  variants: {
    layout: {
      grid: {},
      list: {
        gridColumn: '3',
        gridRow: '1 / span 4',
      },
    },
  },
  defaultVariants: {
    layout: 'grid',
  },
});

export const activityCardTitleRecipe = defineRecipe({
  className: 'activity-card__title',
  base: {
    gridColumn: '1 / -1',
    fontSize: 'font.size.medium',
    marginBlockEnd: 'padding.medium',
    marginInline: 'padding.xsmall',
    lineHeight: '150%',
    fontWeight: 'font.weight.semi.bold',
    display: '-webkit-box',
    lineClamp: '2',
    boxOrient: 'vertical',
    overflow: 'hidden',
  },
  variants: {
    layout: {
      grid: {},
      list: {
        gridColumn: '2',
        gridRow: '2',
        marginInlineStart: 'padding.medium',
        marginInlineEnd: 'padding.3xsmall',
        marginBlockEnd: 'padding.small',
        md: {
          marginInline: 'padding.large',
          marginBlockEnd: 'padding.4xsmall',
        },
      },
    },
    type: {
      default: {},
      highlight: {
        gridColumn: '1',
        gridRow: '2',
        zIndex: '1',
        paddingInline: 'padding.large',
        marginBlockEnd: 'padding.medium',
        marginInline: 'padding.none',
        fontSize: 'font.size.large',
        fontWeight: 'font.weight.bold',
        lineHeight: '138%',
        color: 'text.always.light',
      },
    },
    spacing: {
      default: {},
      compact: {
        marginBlockEnd: 'padding.xsmall',
      },
    },
  },
  defaultVariants: {
    layout: 'grid',
    type: 'default',
    spacing: 'default',
  },
});

export const activityCardTitleLinkRecipe = defineRecipe({
  className: 'activity-card__title-link',
  base: {
    color: 'inherit',
    textDecoration: 'none',
    _after: {
      content: "''",
      position: 'absolute',
      inset: 0,
      zIndex: 0,
    },
  },
});

// Shown only in list layout — a short excerpt below the title.
export const activityCardDescriptionRecipe = defineRecipe({
  className: 'activity-card__description',
  base: {
    gridColumn: '2',
    gridRow: 3,
    fontSize: 'font.size.small',
    lineHeight: '150%',
    color: 'text.secondary',
    marginInline: 'padding.large',
    marginBlockEnd: 'padding.xsmall',
    display: '-webkit-box',
    lineClamp: '2',
    boxOrient: 'vertical',
    overflow: 'hidden',
  },
});

export const activityCardMetaRecipe = defineRecipe({
  className: 'activity-card__meta',
  base: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: 'gap.2xsmall',
    marginInline: 'padding.xsmall',
    fontSize: 'font.size.small',
    lineHeight: '150%',
    color: 'text.tertiary',
  },
  variants: {
    layout: {
      grid: {},
      list: {
        gridColumn: '2',
        gridRow: '4',
        flexDirection: 'row',
        gap: 'gap.none',
        marginInlineStart: 'padding.medium',
        marginInlineEnd: 'padding.3xsmall',
        marginBlockEnd: 'padding.2xsmall',
      },
    },
    type: {
      default: {},
      highlight: {
        gridColumn: '1',
        gridRow: '3',
        zIndex: '1',
        marginInline: 'padding.none',
        fontSize: 'font.size.small',
        lineHeight: '150%',
        color: 'text.always.light.alpha',
        paddingInline: 'padding.large',
        marginBlockEnd: 'padding.medium',
        gap: 'gap.2xsmall',
      },
    },
    spacing: {
      default: {},
      compact: {
        gap: 'gap.4xsmall',
      },
    },
  },
  defaultVariants: {
    layout: 'grid',
    type: 'default',
    spacing: 'default',
  },
});

export const activityCardMetaItemRecipe = defineRecipe({
  className: 'activity-card__meta-item',
  base: {
    display: 'flex',
    flexDirection: 'row',
    gap: 'gap.2xsmall',
    // flex-start so the icon aligns with the first line when MetaItemContent has description rows
    alignItems: 'flex-start',
  },
  variants: {
    layout: {
      grid: {},
      list: {
        paddingInline: 'gap.large',
        _first: { paddingInlineStart: '0' },
        _notFirst: {
          borderInlineStartWidth: 'border.width.small',
          borderInlineStartStyle: 'solid',
          borderColor: 'border.primary',
        },
      },
    },
  },
  defaultVariants: {
    layout: 'grid',
  },
});

// Wraps the label text and optional description inside a MetaItem, beside the icon.
export const activityCardMetaItemContentRecipe = defineRecipe({
  className: 'activity-card__meta-item-content',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'gap.3xsmall',
    flex: '1',
    minInlineSize: '0',
  },
});

// Optional description area rendered below the label inside a MetaItem.
// Use for sub-content like stage rows in the "Etapas da trilha" meta item.
export const activityCardMetaItemDescriptionRecipe = defineRecipe({
  className: 'activity-card__meta-item-description',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'gap.4xsmall',
  },
});

export const activityCardMetaIconRecipe = defineRecipe({
  className: 'activity-card__meta-icon',
  base: {
    flex: '0 0 auto',
  },
});

export const activityCardMetaTextRecipe = defineRecipe({
  className: 'activity-card__meta-text',
  base: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});
